import { useQuery } from "@tanstack/react-query";

import { enabledCustomerCarrierSchema } from "~/shared/schemas/auto-dispatch-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

export const customerEnabledCarriersQueryKey = (customerId: number) =>
	["customerEnabledAutoDispatchCarriers", customerId] as const;

const getEnabledCustomerCarriers = async (
	customerId: number,
	signal?: AbortSignal,
) => {
	const signalOptions = signal ? { signal } : undefined;
	const url = apiRoutes.GETCUSTOMERENABLEDCARRIERS.replace(
		":customerId",
		customerId.toString(),
	);
	const resp = await getAndValidate(
		url,
		enabledCustomerCarrierSchema.array(),
		signalOptions,
	);

	if (resp.success) {
		return resp.data.map((carrier) => ({
			customerId: carrier.customerId,
			carrierId: carrier.carrierId,
			carrierName: carrier.carrierName,
			scac: carrier.scac,
			canAutoDispatch: carrier.isEnabledForCustomer,
		}));
	} else {
		throw new Error(
			`Failed to fetch enabled auto-dispatch carriers: ${resp.error}`,
		);
	}
};

export const useGetCustomerCarriers = (customerId: number) => {
	const {
		data: enabledCarriers,
		error: enabledCarriersError,
		isLoading: enabledCarriersIsLoading,
		isFetching: enabledCarriersIsFetching,
		refetch: refetchEnabledCarriers,
	} = useQuery({
		queryKey: customerEnabledCarriersQueryKey(customerId),
		queryFn: async ({ signal }: { signal: AbortSignal }) =>
			getEnabledCustomerCarriers(customerId, signal),
		enabled: customerId > 0,
		staleTime: 0,
		gcTime: 0,
	});

	return {
		enabledCarriers,
		enabledCarriersError,
		enabledCarriersIsLoading,
		enabledCarriersIsFetching,
		refetchEnabledCarriers,
	};
};
